import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { fetchCancellationDistribution } from "../../../actions/userCancellationActions";

// Period keys MUST match UserCancellationService::SUPPORTED_PERIODS in APIv2.
const PERIODS = [
  { key: "last_30_days", label: "Last 30 days" },
  { key: "last_month", label: "Last month" },
  { key: "current_calendar_year", label: "Current year" },
  { key: "year", label: "Specific year…" },
  { key: "all_time", label: "All time" },
];

// Render a small color-cycling palette so multiple bucket bars/slices stay
// visually distinguishable without configuring per-reason colors.
const palette = (theme) => [
  theme.palette.primary.main,
  theme.palette.success.main,
  theme.palette.warning.main,
  theme.palette.info.main,
  theme.palette.secondary.main,
  theme.palette.error.main,
  "#9c27b0",
  "#00897b",
  "#5c6bc0",
  "#ef6c00",
];

const yearOptions = (() => {
  const currentYear = new Date().getFullYear();
  // Cancellation feature shipped in 2023 — start from there. Capping at the
  // current year keeps the dropdown from suggesting future years that have
  // no data.
  const out = [];
  for (let y = currentYear; y >= 2023; y--) out.push(y);
  return out;
})();

/**
 * @param {Object} props
 * @param {string} props.period — controlled by the parent so the page-level
 *   raw events drill-down can stay in lockstep with the chart.
 * @param {number} props.year
 * @param {(p: string) => void} props.onPeriodChange
 * @param {(y: number) => void} props.onYearChange
 */
const CancellationDistributionChart = ({ period, year, onPeriodChange, onYearChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { loading, data, error } = useSelector(
    (state) => state.user_cancellations.distribution
  );

  // `compareMode` ('off' | 'previous' | 'yoy') and `chartType` stay LOCAL —
  // they only affect the chart's own visualization. The KPI cards above
  // render BOTH comparisons all the time; this selector picks which one
  // appears as a second bar series next to the current period in the chart.
  const [compareMode, setCompareMode] = useState("off");
  const [chartType, setChartType] = useState("bar");

  // Always request comparisons from the API (the parent's KPI cards
  // depend on them being there on every load — they read both
  // `comparisons.previous` and `comparisons.yoy` regardless of what the
  // chart is doing). The API returns nulls for both blocks when the
  // period can't have a comparison (`all_time`), so sending compare=1
  // unconditionally is safe and saves the cards from going dark on the
  // default page load. The CHART's grouped-bars logic gates separately
  // on `compareMode` and `chartType` further down.
  useEffect(() => {
    dispatch(
      fetchCancellationDistribution({
        period,
        year: period === "year" ? year : undefined,
        compare: true,
      })
    );
  }, [dispatch, period, year]);

  // Whether the chart should *visualize* a comparison as a second bar
  // series. KPI cards still see both comparisons regardless.
  const effectiveCompare = compareMode !== "off" && period !== "all_time" && chartType !== "pie";

  const colors = useMemo(() => palette(theme), [theme]);

  const periodLabel = useMemo(() => {
    if (!data?.period) return "";
    if (data.period.key === "all_time") return "All time";
    // Derive the year from the API response (not local state) so the label
    // stays in lockstep with the data being displayed. Otherwise changing
    // the dropdown updates the label immediately while data.total still
    // reflects the previous fetch — e.g. "150 cancellations • Year 2024"
    // where 150 was actually 2023's count.
    if (data.period.key === "year") {
      const responseYear = data.period.from ? new Date(data.period.from).getUTCFullYear() : null;
      return responseYear ? `Year ${responseYear}` : "Year";
    }
    if (!data.period.from || !data.period.to) return PERIODS.find((p) => p.key === data.period.key)?.label || "";
    // UTC-anchored — see CancellationKpiCards.formatPeriodLabel for the
    // off-by-one explanation. API window boundaries are UTC midnights;
    // formatting in local time would surface "March 31" for an "April 1"
    // boundary in any negative-UTC locale.
    const from = new Date(data.period.from).toLocaleDateString(undefined, { timeZone: "UTC" });
    const to = new Date(data.period.to).toLocaleDateString(undefined, { timeZone: "UTC" });
    return `${from} – ${to}`;
  }, [data]);

  // Pick which comparison block (previous or yoy) to draw as the second
  // bar series. KPI cards above the chart show BOTH deltas regardless;
  // this only governs what's visualized inside the bar chart, so it's
  // gated on `effectiveCompare` (off when pie is selected or period is
  // `all_time`) so the comparison summary below the chart and the
  // grouped bars stay in sync with each other.
  const activeComparisonBlock = useMemo(() => {
    if (!data?.comparisons || !effectiveCompare) return null;
    return data.comparisons[compareMode] ?? null;
  }, [data, compareMode, effectiveCompare]);

  // Build a per-cancellation map of comparison counts so the second bar
  // series can be paired up against the current ones. Categories the
  // current period doesn't contain are dropped (we follow the current
  // period's ordering — that's what the user asked the chart to show).
  const comparisonCountByCancellationId = useMemo(() => {
    if (!activeComparisonBlock?.objects) return new Map();
    return new Map(activeComparisonBlock.objects.map((o) => [o.cancellation_id, o.count]));
  }, [activeComparisonBlock]);

  // Pick the longest reason label so we can size the y-axis margin to fit
  // it without ellipsis. ~7px per char is a fine eyeball for MUI's default
  // body2 metrics; capped so a single absurdly long reason doesn't push the
  // bars off the right side.
  const yAxisLeftMargin = useMemo(() => {
    if (!data?.objects?.length) return 200;
    const longest = Math.max(...data.objects.map((o) => (o.reason || '').length));
    return Math.min(360, Math.max(180, longest * 7 + 20));
  }, [data]);

  const renderChart = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          Couldn't load cancellation distribution. Try refreshing or pick a different period.
        </Alert>
      );
    }
    if (!data || data.total === 0) {
      return (
        <Alert severity="info" sx={{ my: 2 }}>
          No cancellations recorded for this period.
        </Alert>
      );
    }

    if (chartType === "pie") {
      const pieData = data.objects.map((o, i) => ({
        id: o.cancellation_id ?? `null-${i}`,
        value: o.count,
        // Legend & tooltip label — keep the percentage in the label so it's
        // visible without hovering over each slice.
        label: `${o.reason || "(Unknown)"} — ${o.percentage}%`,
        color: colors[i % colors.length],
      }));
      return (
        <PieChart
          series={[
            {
              data: pieData,
              innerRadius: 60,
              paddingAngle: 1,
              cornerRadius: 4,
              highlightScope: { fade: "global", highlight: "item" },
              // MUI x-charts hands different shapes to valueFormatter
              // depending on the chart family / version (PieChart sometimes
               // gives the data item, BarChart gives a raw number). Handle
              // both so the tooltip doesn't read "undefined cancellations".
              valueFormatter: (input) => {
                const v = typeof input === "number" ? input : input?.value;
                return `${v ?? 0} cancellation${v === 1 ? "" : "s"}`;
              },
            },
          ]}
          height={420}
          slotProps={{
            legend: {
              direction: "vertical",
              position: { vertical: "middle", horizontal: "right" },
            },
          }}
        />
      );
    }

    // Bar chart — horizontal bars sorted by count desc (data already sorted
    // by the backend). When the user picked a Compare mode AND the API
    // returned that comparison block, render two side-by-side bars per
    // category (current period in primary blue, comparison in muted grey).
    const reasons = data.objects.map((o) => o.reason || "(Unknown)");
    const reversedReasons = [...reasons].reverse();
    const currentCounts = [...data.objects.map((o) => o.count)].reverse();
    const showComparison = !!activeComparisonBlock;
    const comparisonCounts = showComparison
      ? [...data.objects.map((o) => comparisonCountByCancellationId.get(o.cancellation_id) ?? 0)].reverse()
      : null;
    // Increase per-row height a bit when grouping so the two bars per
    // category aren't squished together.
    const rowHeight = showComparison ? 56 : 40;

    const series = [
      {
        data: currentCounts,
        label: showComparison ? "Current period" : "Cancellations",
        color: theme.palette.primary.main,
        valueFormatter: (value, ctx) => {
          const obj = data.objects[data.objects.length - 1 - ctx.dataIndex];
          return `${value} (${obj?.percentage ?? 0}%)`;
        },
      },
    ];
    if (showComparison) {
      series.push({
        data: comparisonCounts,
        label: compareMode === "yoy" ? "Same period last year" : "Previous period",
        // Muted neutral so the current-period bar stays visually dominant.
        color: theme.palette.mode === "dark" ? theme.palette.grey[600] : theme.palette.grey[400],
        valueFormatter: (value) => `${value}`,
      });
    }

    return (
      <BarChart
        layout="horizontal"
        yAxis={[{
          scaleType: "band",
          data: reversedReasons,
          // Explicit width so MUI doesn't truncate long reason strings into
          // "Doe…", "Tec…" etc. when several wide labels share the column.
          width: yAxisLeftMargin,
        }]}
        xAxis={[{ label: "Cancellations" }]}
        series={series}
        height={Math.max(280, reasons.length * rowHeight + 100)}
        margin={{ left: yAxisLeftMargin }}
        slotProps={showComparison ? {
          legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "right" },
          },
        } : undefined}
      />
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Cancellation Reasons</Typography>
          <Typography variant="body2" color="text.secondary">
            {data ? `${data.total} cancellation${data.total === 1 ? "" : "s"} • ${periodLabel}` : "Loading…"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Period</InputLabel>
            <Select label="Period" value={period} onChange={(e) => onPeriodChange(e.target.value)}>
              {PERIODS.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {period === "year" && (
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Year</InputLabel>
              <Select label="Year" value={year} onChange={(e) => onYearChange(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Compare bars to</InputLabel>
            <Select
              label="Compare bars to"
              value={compareMode}
              onChange={(e) => setCompareMode(e.target.value)}
              // Comparison-rendering only meaningful for bar charts and
              // periods that have a previous window (all_time has none).
              // The Compare mode state is preserved across these states so
              // toggling out restores the user's intent.
              disabled={period === "all_time" || chartType === "pie"}
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="previous">Previous period</MenuItem>
              <MenuItem value="yoy">Same period last year</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={chartType}
            onChange={(_, v) => v && setChartType(v)}
          >
            <ToggleButton value="bar">Bar</ToggleButton>
            {/* Pie is unreachable when a comparison is actively rendered:
                two periods don't render as side-by-side wedges. Mirrors
                the Compare selector's disabled state. */}
            <ToggleButton value="pie" disabled={effectiveCompare}>Pie</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {renderChart()}

      {/* The KPI cards above the chart are the canonical comparison
          headline now — this caption is just a labeled context line for
          the comparison series IN the chart so the reader knows what
          period the second bar is. Hidden during loading/error/non-active
          states so we don't flash stale numbers (the reducer preserves
          `data` across loading + error transitions intentionally). */}
      {!loading && !error && activeComparisonBlock && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          {compareMode === "yoy" ? "Same period last year" : "Previous period"} total: {activeComparisonBlock.total}
        </Typography>
      )}
    </Paper>
  );
};

export default CancellationDistributionChart;
